import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import Wordmark from "@/components/Wordmark";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, TrendingUp, Clock, Film, Users, History, Hash, Lock, Coins, Play, Upload as UploadIcon, ChevronRight, Star, Flame, Music, Gamepad2, Smile, GraduationCap, Clapperboard, Trophy, Utensils, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useInfiniteVideos } from "@/lib/useInfinite";
import { ScrollSentinel } from "@/components/ScrollSentinel";

const CATEGORY_ICONS = {
  All: { icon: Clapperboard, color: "#00E7FF" },
  Trending: { icon: Flame, color: "#FF6B00" },
  Music: { icon: Music, color: "#B84BFF" },
  Gaming: { icon: Gamepad2, color: "#22C55E" },
  Comedy: { icon: Smile, color: "#FFD700" },
  Education: { icon: GraduationCap, color: "#FF6D2E" },
  Featured: { icon: Star, color: "#B4A6FF" },
  Tech: { icon: Sparkles, color: "#00E7FF" },
  Sports: { icon: Trophy, color: "#FF3EA5" },
  Vlogs: { icon: Users, color: "#00E7FF" },
  Fitness: { icon: Flame, color: "#FF6B00" },
  Food: { icon: Utensils, color: "#FFD700" },
};

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [recommended, setRecommended] = useState([]);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [premium, setPremium] = useState([]);
  const [hashtags, setHashtags] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/videos/featured").then(r=>setFeatured(r.data)),
      api.get("/videos", {params:{sort:"trending", format:"long", limit:12}}).then(r=>setTrending(r.data)),
      api.get("/videos", {params:{format:"short", limit:10}}).then(r=>setShorts(r.data)),
      api.get("/categories").then(r=>setCats(r.data)),
      api.get("/videos/recommended", {params:{limit:8}}).then(r=>setRecommended(r.data)),
      api.get("/creators/popular", {params:{limit:8}}).then(r=>setPopular(r.data)),
      api.get("/videos", {params:{is_paid:true, sort:"trending", limit:8}}).then(r=>setPremium(r.data)),
      api.get("/hashtags/trending").then(r=>setHashtags(r.data)),
    ]).catch(()=>{});
  }, []);

  const freshParams = {sort:"new", format:"long"};
  if (cat !== "All" && cat !== "Trending" && cat !== "Featured") freshParams.category = cat;
  if (cat === "Trending") freshParams.sort = "trending";
  if (cat === "Featured") freshParams.sort = "featured";
  const { items: fresh, loading: freshLoading, done: freshDone, sentinelRef: freshSentinel } = useInfiniteVideos(freshParams, 12);

  useEffect(() => {
    if (user) api.get("/me/recently-watched").then(r=>setRecent(r.data)).catch(()=>{});
  }, [user]);

  const hero = featured[0];
  const chipList = ["All", "Trending", "Music", "Gaming", "Comedy", "Education", "Featured", ...cats.filter(c => !["All","Music","Gaming","Comedy","Education"].includes(c))];

  return (
    <div className="mx-auto max-w-7xl">
      {/* HERO */}
      <section className="px-3 sm:px-6 pt-4">
        <div className="relative rounded-3xl overflow-hidden border border-[#00E7FF]/25 shadow-[0_0_40px_rgba(0,231,255,0.15)]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1247] via-[#2a1265] to-[#0d0b2b]"/>
          <div className="absolute inset-0"
               style={{background: "radial-gradient(600px 300px at 80% 40%, rgba(255,62,165,0.35), transparent 60%), radial-gradient(500px 300px at 20% 20%, rgba(0,231,255,0.25), transparent 60%)"}}/>
          {/* Sparkle dots */}
          <div className="absolute inset-0 opacity-40" style={{backgroundImage: "radial-gradient(1.5px 1.5px at 20% 30%, #fff, transparent), radial-gradient(1px 1px at 70% 20%, #fff, transparent), radial-gradient(2px 2px at 40% 80%, #fff, transparent), radial-gradient(1px 1px at 85% 65%, #fff, transparent), radial-gradient(1.5px 1.5px at 10% 70%, #fff, transparent), radial-gradient(1px 1px at 55% 45%, #fff, transparent)", backgroundSize: "300px 300px"}}/>
          <div className="relative grid sm:grid-cols-[1.1fr_0.9fr] gap-4 p-5 sm:p-8">
            <div>
              <div className="h-font text-3xl sm:text-4xl font-extrabold leading-tight">Welcome to</div>
              <div className="mt-2"><Wordmark size="lg"/></div>
              <div className="mt-4 space-y-1">
                <div className="h-font text-lg sm:text-2xl font-extrabold">Watch. Upload. Go Live.</div>
                <div className="text-[#00E7FF] h-font italic text-sm sm:text-lg font-semibold">Keep the world in the loop!</div>
              </div>
              <div className="flex flex-wrap gap-3 mt-5">
                {hero && (
                  <Link to={`/watch/${hero.id}`} data-testid="hero-watch-btn"
                        className="jx-btn h-12 px-6 rounded-full bg-[#00E7FF] text-black font-extrabold flex items-center gap-2 hover:brightness-110 shadow-[0_0_20px_rgba(0,231,255,0.4)]">
                    <Play size={16} className="fill-black"/> Watch Now <ChevronRight size={16}/>
                  </Link>
                )}
                <Link to="/upload" data-testid="hero-upload-btn"
                      className="jx-btn h-12 px-6 rounded-full border-2 border-[#FF3EA5]/60 text-white font-bold flex items-center gap-2 hover:bg-[#FF3EA5]/10">
                  <UploadIcon size={16}/> Upload <ChevronRight size={16}/>
                </Link>
              </div>
            </div>
            <div className="relative flex items-center justify-center min-h-[180px]">
              <div className="absolute right-4 top-2 rotate-[8deg] text-[#FF3EA5] h-font italic font-extrabold text-lg sm:text-2xl leading-tight text-right"
                   style={{textShadow: "0 0 12px rgba(255,62,165,0.5)"}}>
                Join<br/>The<br/>Loop!
                <div className="h-0.5 w-16 bg-[#FF3EA5] ml-auto mt-1 rounded-full"/>
              </div>
              <div className="w-40 h-40 sm:w-56 sm:h-56 rounded-full bg-gradient-to-br from-[#FF6B00] via-[#FF3EA5] to-[#B84BFF] flex items-center justify-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-4 rounded-full bg-black/40"/>
                <div className="relative text-6xl sm:text-8xl">🎬</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="px-3 sm:px-6 mt-5">
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3" data-testid="category-chips">
          {chipList.slice(0, 8).map(c => {
            const meta = CATEGORY_ICONS[c] || { icon: MoreHorizontal, color: "#00E7FF" };
            const Icon = meta.icon;
            const active = cat === c;
            return (
              <button key={c} onClick={()=>{ setCat(c); nav(`/category/${encodeURIComponent(c)}`); }} data-testid={`chip-${c}`}
                className={`jx-btn aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 sm:gap-2 transition-all ${active ? 'border-[#00E7FF] bg-[#00E7FF]/10 shadow-[0_0_20px_rgba(0,231,255,0.25)]' : 'border-white/10 bg-black/40 hover:bg-white/5'}`}>
                <Icon size={22} style={{color: meta.color}}/>
                <span className="text-[11px] sm:text-xs font-semibold text-white/85">{c}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* TRENDING */}
      <Section title="Trending Videos" icon={Flame} testid="trending" iconColor="#FF6B00" href="/search?sort=trending">
        {trending.map(v => <VideoCard key={v.id} v={v}/>)}
      </Section>

      {/* FEATURED with LIVE/NEW/HOT badges */}
      {featured.length > 1 && (
        <Section title="Featured" icon={Star} testid="featured" iconColor="#B4A6FF" href="/search?sort=featured">
          {featured.slice(0, 6).map((v, i) => <VideoCard key={v.id} v={v} badge={i===0?"LIVE":i===1?"NEW":i===2?"HOT":null}/>)}
        </Section>
      )}

      {recent.length>0 && (
        <Section title="Recently watched" icon={History} testid="recent" iconColor="#00E7FF">
          {recent.map(v => <VideoCard key={v.id} v={v}/>)}
        </Section>
      )}

      <Section title="Recommended" icon={Sparkles} testid="recommended" iconColor="#00E7FF">
        {recommended.map(v => <VideoCard key={v.id} v={v}/>)}
      </Section>

      {premium.length>0 && (
        <Section title="Premium videos" icon={Lock} testid="premium" iconColor="#FFD700">
          {premium.map(v => <VideoCard key={v.id} v={v}/>)}
        </Section>
      )}

      {/* Loop (Shorts) */}
      <section className="px-3 sm:px-6 mt-10" data-testid="section-shorts-row">
        <div className="flex items-end justify-between mb-3">
          <h2 className="h-font text-2xl font-bold flex items-center gap-2"><Film size={20} className="text-[#FF3EA5]"/> Loop</h2>
          <Link to="/shorts" className="text-xs font-semibold text-[#00E7FF] inline-flex items-center gap-1">See All <ChevronRight size={12}/></Link>
        </div>
        <div className="flex gap-3 overflow-x-auto jx-scroll-x -mx-3 sm:-mx-6 px-3 sm:px-6">
          {shorts.map(s => (
            <Link key={s.id} to={`/shorts?v=${s.id}`} className="shrink-0 w-40 sm:w-44">
              <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-black relative border border-white/5">
                <img src={s.thumbnail_url || undefined} alt={s.title} className="w-full h-full object-cover"/>
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-bold text-[#FF3EA5] flex items-center gap-1"><Film size={10}/> LOOP</div>
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                  <p className="text-xs font-semibold line-clamp-2">{s.title}</p>
                  <p className="text-[10px] text-white/60">@{s.creator?.username}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {popular.length>0 && (
        <section className="px-3 sm:px-6 mt-10" data-testid="section-popular-creators">
          <h2 className="h-font text-2xl font-bold flex items-center gap-2 mb-4"><Users size={20} className="text-[#00E7FF]"/> Popular creators</h2>
          <div className="flex gap-4 overflow-x-auto jx-scroll-x -mx-3 sm:-mx-6 px-3 sm:px-6">
            {popular.map(c=>(
              <Link key={c.id} to={`/u/${c.id}`} className="shrink-0 w-32 text-center">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#00E7FF] to-[#FF3EA5] flex items-center justify-center text-black text-3xl font-extrabold overflow-hidden">
                  {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover"/> : c.username[0].toUpperCase()}
                </div>
                <div className="mt-2 text-sm font-semibold truncate">@{c.username}</div>
                <div className="text-xs text-white/50">{c.followers} followers</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {hashtags.length>0 && (
        <section className="px-3 sm:px-6 mt-10">
          <h2 className="h-font text-2xl font-bold flex items-center gap-2 mb-4"><Hash size={20} className="text-[#00E7FF]"/> Trending hashtags</h2>
          <div className="flex gap-2 flex-wrap">
            {hashtags.map(h=>(
              <Link key={h.tag} to={`/search?hashtag=${h.tag}`} data-testid={`hashtag-${h.tag}`}
                    className="chip jx-btn px-4 h-10 rounded-full text-sm font-semibold text-[#00E7FF] hover:bg-[#00E7FF]/10">
                #{h.tag} <span className="text-white/40 text-xs ml-1">{h.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Section title="Fresh drops" icon={Clock} testid="fresh" iconColor="#00E7FF">
        {fresh.map(v => <VideoCard key={v.id} v={v}/>)}
      </Section>
      <ScrollSentinel sentinelRef={freshSentinel} loading={freshLoading} done={freshDone} count={fresh.length} testid="fresh-sentinel"/>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children, iconColor, href }) {
  return (
    <section className="px-3 sm:px-6 mt-10" data-testid={`section-${testid}`}>
      <div className="flex items-end justify-between mb-4">
        <h2 className="h-font text-2xl font-bold flex items-center gap-2">
          <Icon size={22} style={{color: iconColor || "#00E7FF"}}/> {title}
        </h2>
        {href && <Link to={href} className="text-sm font-semibold text-[#00E7FF] inline-flex items-center gap-1">See All <ChevronRight size={14}/></Link>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">{children}</div>
    </section>
  );
}
